import { createContext, useCallback, useEffect, useState } from 'react'
import { User, Session, AuthError, Provider } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { getUserProfile, type UserProfile } from '@/lib/userProfile'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  profile: UserProfile | null
  refreshProfile: () => Promise<void>
  requestEmailOtp: (email: string, redirectTo?: string) => Promise<{ error: AuthError | null }>
  verifyEmailOtp: (email: string, token: string) => Promise<{ error: AuthError | null }>
  signInWithOAuth: (provider: Provider, redirectTo?: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  deleteAccount: () => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export { AuthContext }

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)

  const refreshProfile = useCallback(async () => {
    try {
      const nextProfile = await getUserProfile()
      setProfile(nextProfile)
    } catch (error) {
      console.error('Error loading user profile:', error)
      setProfile(null)
    }
  }, [])

  useEffect(() => {
    // Get initial session and verify user exists
    const initializeAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession()

      if (session) {
        // Verify the user still exists in Supabase
        const { error } = await supabase.auth.getUser()

        if (error?.message?.includes('User from sub claim in JWT does not exist')) {
          // User was deleted, clear the stale session
          console.log('Detected deleted user on initialization, clearing session...')
          await supabase.auth.signOut()
          setSession(null)
          setUser(null)
          setLoading(false)
          return
        }
      }

      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        await refreshProfile()
      } else {
        setProfile(null)
      }

      setLoading(false)
    }

    initializeAuth()

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setLoading(true)
      setSession(session)
      setUser(session?.user ?? null)

      if (session?.user) {
        void refreshProfile().finally(() => setLoading(false))
        return
      }

      setProfile(null)
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [refreshProfile])

  const requestEmailOtp = async (email: string, redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: redirectTo
          ? `${window.location.origin}${redirectTo}`
          : `${window.location.origin}/`,
      },
    })

    return { error }
  }

  const verifyEmailOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    return { error }
  }

  const signInWithOAuth = async (provider: Provider, redirectTo?: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectTo
          ? `${window.location.origin}${redirectTo}`
          : `${window.location.origin}/`,
      },
    })

    return { error }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const deleteAccount = async () => {
    try {
      // Delete the user's account using Supabase Admin API
      // Note: This requires a server-side function or Admin API access
      const { error } = await supabase.rpc('delete_user_account')

      if (error) {
        return { error: new Error(error.message) }
      }

      // Sign out after successful deletion
      await signOut()

      return { error: null }
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Failed to delete account') }
    }
  }

  const value = {
    user,
    session,
    loading,
    profile,
    refreshProfile,
    requestEmailOtp,
    verifyEmailOtp,
    signInWithOAuth,
    signOut,
    deleteAccount,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
